import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import CollectionsIcon from '@mui/icons-material/Collections';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { CATEGORY_IMAGES, type CategorySlug } from '../../assets/categoryImages';
import { imageSrcSet } from '../../utils/images';
import { trackProviderEvent } from '../../services/tracking.service';
import type { ProviderSummary } from '../../types';

type Props = {
  provider: ProviderSummary;
  /**
   * Name of the city the visitor searched — used to render a travelling
   * provider's "Travels to {city}" line. Omit on non-search surfaces
   * (home page, featured strip): a travel hit then falls back to a
   * generic "Travels to this city" label.
   */
  searchedCityName?: string;
};

const FALLBACK_ROTATION: CategorySlug[] = ['hair', 'cake', 'makeup', 'nail', 'fashion', 'event'];

function pickFallbackImage(seedString: string): string {
  // Simple deterministic hash so each provider gets a stable fallback image.
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    hash = (hash * 31 + seedString.charCodeAt(i)) | 0;
  }
  const slug = FALLBACK_ROTATION[Math.abs(hash) % FALLBACK_ROTATION.length];
  const list = CATEGORY_IMAGES[slug];
  if (!list || list.length === 0) return '';
  return list[Math.abs(hash >> 4) % list.length];
}

export function ProviderCard({ provider, searchedCityName }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isTravelProvider = provider.travelProvider === true;
  const travelsToLabel =
    (searchedCityName
      ? t('search.travelsTo', { city: searchedCityName })
      : t('search.travelsHere'))
    + (provider.distanceKm != null ? ` · ${provider.distanceKm} km` : '');

  const initials = provider.businessName
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const cover = provider.coverImageUrl || pickFallbackImage(provider.id);
  const coverSource = imageSrcSet(cover);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'transform 250ms ease, box-shadow 250ms ease, border-color 250ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: (theme) => theme.palette.primary.main,
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 18px 36px rgba(0,0,0,0.55)'
              : '0 18px 36px rgba(26,24,22,0.12)',
        },
      }}
    >
      <CardActionArea
        onClick={() => {
          // Engagement signal for the ranking system (fire-and-forget).
          trackProviderEvent(provider.id, 'PROFILE_CLICK');
          navigate(`/providers/${provider.id}`);
        }}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          textAlign: 'left',
        }}
      >
        <Box sx={{ position: 'relative', aspectRatio: '4 / 3', overflow: 'hidden' }}>
          {cover ? (
            <Box
              component="img"
              src={coverSource.src}
              srcSet={coverSource.srcSet}
              sizes="(max-width:600px) 92vw, (max-width:900px) 46vw, 360px"
              alt={provider.businessName}
              loading="lazy"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 400ms ease',
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: 'common.white',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 800,
                fontSize: '2.5rem',
                letterSpacing: '0.05em',
              }}
            >
              {initials || provider.businessName.slice(0, 2).toUpperCase()}
            </Box>
          )}

          {provider.portfolioCount > 0 && (
            <Chip
              icon={<CollectionsIcon sx={{ fontSize: '0.95rem !important' }} />}
              label={provider.portfolioCount}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: 'common.white',
                backdropFilter: 'blur(4px)',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'common.white' },
              }}
            />
          )}
        </Box>

        <Box sx={{ p: 2.25, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.125rem', mb: 0.75 }}
            noWrap
          >
            {provider.businessName}
          </Typography>

          {isTravelProvider ? (
            // V48 travelling provider — based elsewhere, covers this city.
            // Shown distinctly so the visitor never mistakes it for a local.
            <Stack spacing={0.5} sx={{ mb: 1.25 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ExploreOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary', fontSize: '1rem' }}
                />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {t('search.basedIn', { city: provider.city.name })}
                </Typography>
              </Stack>
              <Chip
                label={travelsToLabel}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: (theme) => `${theme.palette.secondary.main}1A`,
                  color: 'secondary.main',
                }}
              />
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.25 }}>
              <LocationOnIcon
                fontSize="small"
                sx={{ color: 'text.secondary', fontSize: '1rem' }}
              />
              <Typography variant="body2" color="text.secondary" noWrap>
                {provider.city.name}, {provider.city.country}
              </Typography>
            </Stack>
          )}

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            <StorefrontIcon sx={{ color: 'primary.main', fontSize: '1rem' }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {t('search.services', { count: provider.serviceCount })}
            </Typography>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
}
